import React from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * KpiCard - Compact KPI display card
 * 
 * @param {string} label - KPI label (small, uppercase)
 * @param {string|number} value - Main value to display
 * @param {string} format - Value format: 'currency' | 'number' | 'percent' | 'text'
 * @param {number} previousValue - Previous period value (for trend)
 * @param {string} subtitle - Optional subtitle text
 * @param {React.ElementType} icon - Optional icon component
 * @param {string} variant - Color variant: 'default' | 'success' | 'warning' | 'danger' | 'info'
 * @param {string} className - Additional classes
 */
export function KpiCard({
  label,
  value,
  format = 'text',
  previousValue,
  subtitle,
  icon: Icon,
  variant = 'default',
  className,
  onClick,
  ...props
}) {
  // Format value based on type
  const formatValue = (val) => {
    if (val === null || val === undefined) return '—';
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('pt-PT', { 
          style: 'currency', 
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(val);
      case 'number':
        return new Intl.NumberFormat('pt-PT').format(val);
      case 'percent':
        return `${val.toFixed(1)}%`;
      default:
        return val;
    }
  };

  // Calculate trend
  const trend = previousValue !== undefined && previousValue !== 0
    ? ((value - previousValue) / Math.abs(previousValue)) * 100
    : null;

  const variantStyles = {
    default: 'bg-white border-slate-200',
    success: 'bg-emerald-50/50 border-emerald-200',
    warning: 'bg-amber-50/50 border-amber-200',
    danger: 'bg-red-50/50 border-red-200',
    info: 'bg-blue-50/50 border-blue-200',
  };

  const iconVariantStyles = {
    default: 'bg-slate-100 text-slate-600',
    success: 'bg-emerald-100 text-emerald-600',
    warning: 'bg-amber-100 text-amber-600',
    danger: 'bg-red-100 text-red-600',
    info: 'bg-blue-100 text-blue-600',
  };

  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      className={cn(
        "p-4 rounded-lg border transition-all",
        variantStyles[variant],
        onClick && "cursor-pointer hover:shadow-md active:scale-[0.98]",
        className
      )}
      onClick={onClick}
      data-testid="kpi-card"
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider truncate">
            {label}
          </p>
          <p className="text-2xl font-bold text-slate-900 mt-1 font-mono tabular-nums">
            {formatValue(value)}
          </p>
          
          {/* Trend indicator */}
          {trend !== null && (
            <div className="flex items-center gap-1 mt-1">
              {Math.abs(trend) < 0.5 ? (
                <Minus className="h-3 w-3 text-slate-400" />
              ) : trend > 0 ? (
                <TrendingUp className="h-3 w-3 text-emerald-500" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500" />
              )}
              <span className={cn(
                "text-xs font-medium",
                Math.abs(trend) < 0.5 ? "text-slate-400" :
                trend > 0 ? "text-emerald-600" : "text-red-600"
              )}>
                {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
              </span>
            </div>
          )}
          
          {subtitle && (
            <p className="text-xs text-slate-500 mt-1 truncate">
              {subtitle}
            </p>
          )}
        </div>
        
        {Icon && (
          <div className={cn(
            "p-2 rounded-lg flex-shrink-0",
            iconVariantStyles[variant]
          )}>
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </div>
        )}
      </div>
    </Wrapper>
  );
}

/**
 * KpiCardCompact - Even more compact version for mobile
 */
export function KpiCardCompact({
  label,
  value,
  icon: Icon,
  variant = 'default',
  className,
  ...props
}) {
  const bgStyles = {
    default: 'bg-slate-50',
    success: 'bg-emerald-50',
    warning: 'bg-amber-50',
    danger: 'bg-red-50',
    info: 'bg-blue-50',
  };

  const iconStyles = {
    default: 'text-slate-500',
    success: 'text-emerald-500',
    warning: 'text-amber-500',
    danger: 'text-red-500',
    info: 'text-blue-500',
  };

  return (
    <div
      className={cn(
        "flex-1 rounded-lg px-3 py-2 flex items-center gap-2",
        bgStyles[variant],
        className
      )}
      data-testid="kpi-card-compact"
      {...props}
    >
      {Icon && <Icon className={cn("h-4 w-4", iconStyles[variant])} />}
      <div>
        <p className="text-lg font-bold text-slate-800 leading-none font-mono tabular-nums">
          {value}
        </p>
        <p className="text-[10px] text-slate-500 uppercase tracking-wide">
          {label}
        </p>
      </div>
    </div>
  );
}

export default KpiCard;
